import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { CommissionRuleView, OrderSource } from "../../api/types";

const SOURCE_LABELS: Record<OrderSource, string> = {
  MANUAL: "Other (recorded by staff, incl. in-shop orders)",
  PHONE: "Phone orders",
  WHATSAPP: "WhatsApp orders",
  INSTAGRAM: "Instagram orders",
  SHOPIFY: "Shopify orders",
  GLOVO: "Glovo orders",
  API: "API orders",
  EMAIL: "E-mail orders",
};

/**
 * G-22: commission by order source — OWNER ONLY (the endpoint refuses
 * everyone else; this card must simply never render for a manager).
 * A source nobody switched on earns no commission: the fail-safe is the
 * default, so a future marketplace source can never silently pay out.
 */
export function CommissionRulesCard() {
  const [rules, setRules] = useState<CommissionRuleView[]>([]);
  const [busySource, setBusySource] = useState<OrderSource | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listCommissionRules()
      .then(setRules)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Could not load rules."),
      );
  }, []);

  const toggle = async (rule: CommissionRuleView) => {
    setBusySource(rule.source);
    setError(null);
    try {
      const next = await api.setCommissionRule(
        rule.source,
        !rule.commissionEnabled,
      );
      setRules((cur) =>
        cur.map((r) =>
          r.source === next.source
            ? { ...r, commissionEnabled: next.commissionEnabled }
            : r,
        ),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save the rule.");
    } finally {
      setBusySource(null);
    }
  };

  return (
    <section className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <h3 style={{ margin: 0 }}>Commission by order source</h3>
      <p className="muted" style={{ margin: 0 }}>
        Order sources that pay commission · walk-in sales always do
      </p>
      {error ? <div className="alert error">{error}</div> : null}
      {rules.map((rule) => (
        <label key={rule.source} className="hint" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={rule.commissionEnabled}
            disabled={busySource !== null}
            onChange={() => void toggle(rule)}
          />
          <span>{SOURCE_LABELS[rule.source] ?? rule.source}</span>
          {rule.updatedBy ? (
            <small className="muted">set by {rule.updatedBy.fullName}</small>
          ) : null}
        </label>
      ))}
    </section>
  );
}
